import { ReactNode, createContext, useContext } from 'react'
import { z } from 'zod'
import { Form } from '$components'
import { UpdateLocationFormControls } from './controls'
import { UpdateLocationFormContent } from './content'

export enum Input {
  Name = 'name',
  Address = 'address',
  AddressLineOne = 'line_one',
  AddressLineTwo = 'line_two',
  AddressCity = 'city',
  AddressPostCode = 'post_code',
  AddressCountry = 'country',
  BusinessHours = 'business_hours',
}

export const updateLocationSchema = z.object({
  [Input.Name]: z.string()
    .min(1, { message: 'Name is required.' })
    .max(255, { message: 'Name must be less than 255 characters.' }),
  [Input.Address]: z.object({
    [Input.AddressLineOne]: z.string()
      .min(1, { message: 'Address line 1 is required.' })
      .max(255, { message: 'Address line 1 must be less than 255 characters.' }),
    [Input.AddressLineTwo]: z.string()
      .max(255, { message: 'Address line 2 must be less than 255 characters.' })
      .nullable()
      .optional(),
    [Input.AddressCity]: z.string()
      .min(1, { message: 'City is required.' })
      .max(255, { message: 'City must be less than 255 characters.' }),
    [Input.AddressPostCode]: z.string()
      .min(1, { message: 'Post code is required.' })
      .max(255, { message: 'Post code must be less than 255 characters.' }),
    [Input.AddressCountry]: z.string()
      .min(1, { message: 'Country is required.' })
      .max(255, { message: 'Country must be less than 255 characters.' }),
  }),
  [Input.BusinessHours]: z.array(
    z.object({
      id: z.number().optional(),
      day_of_week: z.number().min(0).max(6),
      open: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, { message: 'Invalid time format. Expected HH:MM.' }),
      close: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, { message: 'Invalid time format. Expected HH:MM.' }),
    }),
  ),
})

export type UpdateLocationSchema = z.infer<typeof updateLocationSchema>

type UpdateLocationFormContextType = {
  onSubmit: (values: UpdateLocationSchema) => void
  onCancel: () => void
  isLoading?: boolean
}

const UpdateLocationFormContext = createContext<UpdateLocationFormContextType | undefined>(undefined)

type UpdateLocationFormProps = UpdateLocationFormContextType & {
  children?: ReactNode
  defaultValues?: Partial<UpdateLocationSchema>
}

export function UpdateLocationForm({
  onSubmit,
  onCancel,
  isLoading = false,
  defaultValues,
  children,
}: UpdateLocationFormProps) {
  const context: UpdateLocationFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
  }

  return (
    <UpdateLocationFormContext.Provider value={context}>
      <Form<UpdateLocationSchema, typeof updateLocationSchema>
        schema={updateLocationSchema}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {children}
      </Form>
    </UpdateLocationFormContext.Provider>
  )
}

UpdateLocationForm.Content = UpdateLocationFormContent
UpdateLocationForm.Controls = UpdateLocationFormControls

export function useUpdateLocationForm() {
  const context = useContext(UpdateLocationFormContext)

  if (context === undefined) {
    throw new Error('useUpdateLocationForm must be used within an UpdateLocationFormProvider')
  }

  return context
}
